"use client";

import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "motion/react";

const facts = [
  {
    value: 1,
    secondaryValue: 9,
    label: "1 in 9 Indians",
    description: "Indians may develop cancer in their lifetime.",
  },
  {
    value: 9,
    prefix: "~",
    suffix: " lakh",
    label: "Approximately 9 lakh cancer deaths",
    description: "cancer deaths recorded in India in 2019.",
  },
  {
    value: 3,
    prefix: "₹",
    suffix: " lakh",
    label: "3 lakh rupees",
    description: "is the average cost of cancer treatment.",
  },
] as const;

function useCounter(
  target: number,
  started: boolean,
  reduceMotion: boolean,
  decimalPlaces = 0,
) {
  // Keep the real value in the server-rendered HTML. Only begin changing it
  // after the section enters the viewport, so a missing observer never leaves
  // the page showing zeroes.
  const [value, setValue] = useState(target);

  useEffect(() => {
    if (!started || reduceMotion || target <= 0) return;

    let frame = 0;
    let startedAt = 0;
    const duration = 1450;

    const tick = (timestamp: number) => {
      if (!startedAt) startedAt = timestamp;
      const progress = Math.min((timestamp - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Number((target * eased).toFixed(decimalPlaces)));

      if (progress < 1) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [decimalPlaces, reduceMotion, started, target]);

  return reduceMotion ? target : value;
}

function FactNumber({
  value,
  secondaryValue,
  prefix,
  suffix,
  decimalPlaces = 0,
  label,
  started,
  reduceMotion,
}: {
  value: number;
  secondaryValue?: number;
  prefix?: string;
  suffix?: string;
  decimalPlaces?: number;
  label: string;
  started: boolean;
  reduceMotion: boolean;
}) {
  const primary = useCounter(value, started, reduceMotion, decimalPlaces);
  const secondary = useCounter(secondaryValue ?? 0, started, reduceMotion);
  const formattedPrimary = primary.toLocaleString("en-IN", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });

  return (
    <p
      className="font-sans text-[clamp(4rem,5.6vw,6.5rem)] font-semibold leading-[0.82] tracking-[-0.07em] text-[#203b42] tabular-nums"
      aria-label={label}
    >
      <span aria-hidden>
        {prefix ? (
          <span className={prefix === "₹" ? "" : "text-[0.58em] tracking-[-0.02em]"}>
            {prefix}
          </span>
        ) : null}
        {formattedPrimary}
        {secondaryValue ? (
          <>
            <span className="mx-[0.18em] text-[0.42em] font-medium tracking-normal">in</span>
            {secondary}
          </>
        ) : (
          <span className="text-[0.42em] font-medium tracking-normal">{suffix}</span>
        )}
      </span>
    </p>
  );
}

export function CancerFacts() {
  const statsRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion() ?? false;
  const [started, setStarted] = useState(false);
  const counterStarted = reduceMotion || started;

  useEffect(() => {
    const stats = statsRef.current;
    if (!stats) return;

    if (reduceMotion) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setStarted(true);
        observer.disconnect();
      },
      { threshold: 0.25 },
    );

    observer.observe(stats);
    return () => observer.disconnect();
  }, [reduceMotion]);

  return (
    <section className="cancer-facts relative bg-transparent text-[#1a2426]">
      <div className="relative z-10 mx-auto max-w-[1536px] px-5 sm:px-8 lg:px-12 xl:px-16">
        <div className="py-20 md:py-28">
          <h2 className="max-w-[1040px] font-display text-[clamp(2.4rem,3.4vw,3.8rem)] leading-[1.04] tracking-[-0.03em] text-[#1d315f]">
            Cancer is becoming a family fact
            <span className="hero-gradient-text -mb-[0.12em] block pb-[0.12em]">
              and we&apos;re here to change it.
            </span>
          </h2>

          <div
            ref={statsRef}
            className="mt-16 grid border-t border-[#b8c9c6] md:mt-24 md:grid-cols-3"
          >
            {facts.map((fact, index) => (
              <article
                key={fact.label}
                className={`py-10 md:min-h-[285px] md:px-8 md:py-12 lg:px-10 ${
                  index > 0 ? "border-t border-[#b8c9c6] md:border-l md:border-t-0" : ""
                }`}
              >
                <FactNumber
                  value={fact.value}
                  secondaryValue={"secondaryValue" in fact ? fact.secondaryValue : undefined}
                  prefix={"prefix" in fact ? fact.prefix : undefined}
                  suffix={"suffix" in fact ? fact.suffix : undefined}
                  label={fact.label}
                  started={counterStarted}
                  reduceMotion={reduceMotion}
                />
                <p className="mt-8 max-w-[21rem] font-sans text-base leading-7 text-[#45575a] sm:text-lg sm:leading-8">
                  {fact.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
